import { useEffect } from "react";
import { Toaster } from "@/components/ui/sonner";
import useReveal from "@/hooks/useReveal";
import { setPageSEO } from "@/lib/seo";
import Header from "@/components/landing/Header";
import Footer from "@/components/landing/Footer";
import WhatsAppFloat from "@/components/landing/WhatsAppFloat";
import SubpageHero from "@/components/landing/SubpageHero";
import BeforeAfter from "@/components/landing/BeforeAfter";
import StatsBanner from "@/components/landing/StatsBanner";
import FinalCTA from "@/components/landing/FinalCTA";
import { Users, Eye, AlertTriangle, Layers, Zap, CalendarCheck, Target, BarChart3, Clock } from "lucide-react";

const BRAKES = [
  { icon: Users, title: "Procesos que dependen de personas", text: "Cuando la persona clave se va o está ausente, el proceso se detiene. No hay documentación, no hay estructura que sostenga la operación." },
  { icon: Eye, title: "Gestión sin métricas reales", text: "Se toman decisiones con datos incompletos o por intuición. Lo que no se mide, no se puede mejorar." },
  { icon: CalendarCheck, title: "Reuniones largas, decisiones pocas", text: "Horas de reunión, acuerdos vagos y compromisos que no se cumplen. El tiempo de los líderes se diluye sin resultado." },
  { icon: AlertTriangle, title: "Prioridades que cambian cada semana", text: "Sin un foco claro, cada urgencia se convierte en la nueva prioridad. El equipo opera en modo reactivo permanente." },
  { icon: Layers, title: "Duplicación de esfuerzo", text: "Dos áreas trabajando en lo mismo sin saberlo. Recursos desperdiciados por falta de coordinación y visibilidad." },
  { icon: Zap, title: "Comunicación fragmentada", text: "Información crítica que no llega donde tiene que llegar. Decisiones tomadas con datos obsoletos o incompletos." },
];

const PILLARS = [
  {
    icon: Target,
    num: "01",
    title: "Claridad",
    text: "Cuando todos saben exactamente qué importa y para qué, nadie pierde tiempo en lo que no mueve la aguja. EOS instala una Visión compartida que guía cada decisión diaria.",
  },
  {
    icon: CalendarCheck,
    num: "02",
    title: "Disciplina de ejecución",
    text: "EOS introduce rituales semanales, trimestrales y anuales que mantienen al equipo enfocado. No hay eficiencia sostenible sin una estructura de ejecución clara.",
  },
  {
    icon: BarChart3,
    num: "03",
    title: "Accountability real",
    text: "Cada función tiene un responsable. Cada métrica tiene una persona que la defiende. EOS elimina las zonas grises de responsabilidad que generan ineficiencia.",
  },
];

export default function EficienciaOperativa() {
  useReveal();

  useEffect(() => {
    setPageSEO({
      title: "Eficiencia Operativa Empresarial con EOS | Carlos Garcín Implementador EOS Chile",
      description: "Descubre cómo EOS logra eficiencia operativa real: procesos claros, equipos alineados y ejecución disciplinada. Carlos Garcín, Implementador EOS en Chile.",
      path: "/eficiencia-operativa-empresas",
      keywords: "eficiencia operativa empresarial, EOS eficiencia operativa, procesos empresariales Chile, accountability equipos",
    });
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Header />
      <main>
        <SubpageHero
          eyebrow="EOS y Eficiencia Operativa Empresarial"
          title="Cuando EOS entra, la empresa deja de correr en círculos."
          subtitle="La mayoría de las empresas tienen personas comprometidas. El problema no es la gente — es la ausencia de estructura, foco y disciplina. La metodología EOS resuelve eso, paso a paso."
          withCta
        />

        {/* Los frenos de la eficiencia */}
        <section data-testid="eficiencia-brakes" className="section-padding bg-slate-50/60">
          <div className="container-eos">
            <div className="max-w-3xl reveal">
              <span className="eyebrow">El diagnóstico</span>
              <h2 className="mt-3 font-display font-extrabold text-3xl sm:text-4xl lg:text-5xl text-navy-800 leading-tight">
                ¿Por qué es tan difícil{" "}
                <span className="text-[#F26522]">ser eficiente?</span>
              </h2>
              <p className="mt-5 text-lg text-slate-600 max-w-2xl">
                Estos son los seis frenos que más limitan la eficiencia operativa en empresas medianas de Chile y Latinoamérica.
              </p>
            </div>
            <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {BRAKES.map((b, i) => (
                <article
                  key={i}
                  data-testid={`eficiencia-brake-${i + 1}`}
                  className="card-soft p-5 reveal"
                  style={{ transitionDelay: `${i * 70}ms` }}
                >
                  <div className="flex items-center justify-between">
                    <div className="w-9 h-9 rounded-lg bg-[#F26522]/10 flex items-center justify-center">
                      <b.icon className="w-4 h-4 text-[#F26522]" strokeWidth={1.7} />
                    </div>
                    <span className="font-display font-bold text-slate-200 text-xl tabular-nums">0{i + 1}</span>
                  </div>
                  <h3 className="mt-4 font-display font-bold text-base text-navy-800 leading-snug">{b.title}</h3>
                  <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">{b.text}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* Las 3 palancas de EOS */}
        <section data-testid="eficiencia-pillars" className="section-padding bg-white">
          <div className="container-eos">
            <div className="max-w-3xl reveal">
              <span className="eyebrow">La solución EOS</span>
              <h2 className="mt-3 font-display font-extrabold text-3xl sm:text-4xl lg:text-5xl text-navy-800 leading-tight">
                Las 3 palancas que EOS activa{" "}
                <span className="text-[#F26522]">en tu empresa</span>
              </h2>
              <p className="mt-5 text-lg text-slate-600 max-w-2xl">
                EOS no añade complejidad — elimina las causas raíz de la ineficiencia y reemplaza el caos con una estructura simple y efectiva.
              </p>
            </div>
            <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
              {PILLARS.map((p, idx) => (
                <article
                  key={idx}
                  data-testid={`eficiencia-pillar-${idx + 1}`}
                  className="reveal relative rounded-2xl border border-slate-200 bg-white p-8 transition-all duration-300 hover:border-[#F26522]/40 hover:shadow-xl hover:-translate-y-1 overflow-hidden"
                  style={{ transitionDelay: `${idx * 90}ms` }}
                >
                  <div className="absolute top-0 inset-x-0 h-1 bg-gradient-to-r from-[#F26522] to-[#F26522]/40" />
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-lg bg-navy-800 flex items-center justify-center shadow-md">
                      <p.icon className="w-5 h-5 text-white" strokeWidth={1.7} />
                    </div>
                    <span className="text-xs font-bold tracking-[0.2em] uppercase text-[#F26522]">
                      {p.num} / 03
                    </span>
                  </div>
                  <h3 className="mt-6 font-display font-extrabold text-2xl text-navy-800">{p.title}</h3>
                  <p className="mt-3 text-slate-600 leading-relaxed">{p.text}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <BeforeAfter />
        <StatsBanner />
        <FinalCTA />
      </main>
      <Footer />
      <WhatsAppFloat />
      <Toaster position="top-right" />
    </>
  );
}
